import type {
  ToastManagerAddOptions as BaseAddOptions,
  ToastObject as BaseToastObject,
  ToastManagerUpdateOptions as BaseUpdateOptions,
  Toast,
} from '@base-ui/react/toast'
import type { ComponentProps } from 'react'
import type { WithClassName } from '#components/_primitives/base-ui-types'

export type SwipeDirection = 'up' | 'down' | 'left' | 'right'

export const ToastVariant = {
  Informational: 'informational',
  Success: 'success',
  Error: 'error',
} as const

export type ToastVariant = (typeof ToastVariant)[keyof typeof ToastVariant]

export const ToastPosition = {
  TopLeft: 'top-left',
  TopCenter: 'top-center',
  TopRight: 'top-right',
  BottomLeft: 'bottom-left',
  BottomCenter: 'bottom-center',
  BottomRight: 'bottom-right',
} as const

export type ToastPosition = (typeof ToastPosition)[keyof typeof ToastPosition]

/** Toast provider. Controls toast context - no HTML element rendered. */
export interface ToastProviderProps
  extends Omit<ComponentProps<typeof Toast.Provider>, 'toastManager'> {}

/** Fixed viewport container for stacking toasts. */
export interface ToastViewportProps
  extends WithClassName<typeof Toast.Viewport> {
  /** Screen position for the toast viewport. Defaults to `bottom-right`. */
  position?: ToastPosition
}

/** Individual toast root with swipe-to-dismiss and animations. */
export interface ToastRootProps extends WithClassName<typeof Toast.Root> {
  /** Screen position - controls enter/exit/stack animation direction. Defaults to `bottom-right`. */
  position?: ToastPosition
}

/** Styled content card within a toast. */
export interface ToastContentProps
  extends WithClassName<typeof Toast.Content> {}

/** Accessible toast title. Renders an `<h2>`. */
export interface ToastTitleProps extends WithClassName<typeof Toast.Title> {}

/** Toast description. Renders a `<p>`. */
export interface ToastDescriptionProps
  extends WithClassName<typeof Toast.Description> {}

/**
 * Action button props, matching Banner's action pattern.
 * @public — toast action button API
 */
export interface ToastActionProps {
  /** Label text for the action button. */
  label: string
  /** Click handler. */
  onClick: () => void | Promise<void>
  /** Shows a spinner and disables the button while true. */
  isLoading?: boolean
}

/** Portal container for toasts. */
export interface ToastPortalProps extends ComponentProps<typeof Toast.Portal> {}

/** Custom data consumers can attach to a toast via the `data` field. */
export interface ToastData {
  /** Optional action button rendered in the toast. */
  action?: ToastActionProps
}

type WithRequiredTitle<T> = T & { title: React.ReactNode }

/**
 * Adds an `autoDismiss` boolean (default `true`) on top of Base UI's options.
 * When `false`, the toast stays until explicitly closed (timeout: 0).
 */
type WithAutoDismiss<T> = T & {
  /** Whether the toast auto-dismisses after the provider's default timeout. Defaults to `true`. */
  autoDismiss?: boolean
}

/** Add options with title required. */
export type ToastManagerAddOptions<Data extends object = ToastData> =
  WithAutoDismiss<WithRequiredTitle<BaseAddOptions<Data>>>

/** Update options - permissive since the toast already has a title. */
export type ToastManagerUpdateOptions<Data extends object = ToastData> =
  BaseUpdateOptions<Data>

/** Promise options where string values map to `title` (not Base UI's `description`). */
export interface ToastManagerPromiseOptions<
  Value,
  Data extends object = ToastData,
> {
  loading: string | WithRequiredTitle<BaseUpdateOptions<Data>>
  success:
    | string
    | WithRequiredTitle<BaseUpdateOptions<Data>>
    | ((result: Value) => string | WithRequiredTitle<BaseUpdateOptions<Data>>)
  error:
    | string
    | WithRequiredTitle<BaseUpdateOptions<Data>>
    | ((error: unknown) => string | WithRequiredTitle<BaseUpdateOptions<Data>>)
}

/** @public — re-export of Base UI ToastObject */
export type { BaseToastObject as ToastObject }
